import { Component, computed, inject, signal } from '@angular/core';
import { Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApiService } from '@solcre-org/core';
import { LoaderService, AlertService, HeaderService, PermissionWrapperService, ModalMode, PermissionsResources, PermissionsActions, ButtonConfig, ButtonType, ModalFieldConfig, FieldType, DynamicFieldDirective, GenericButtonComponent, AlertType, HeaderConfigurationService } from '@solcre-org/core-ui';
import { environment } from '../../../../environments/environment.local';
import { ConfigurationModel } from '../../../shared/models/configuration.model';
import { DateUtils } from '../../../shared/utils/date.utils';
@Component({
  selector: 'app-configuration',
  imports: [TranslateModule, DynamicFieldDirective, GenericButtonComponent],
  templateUrl: './configuration.html'
})
export class Configuration {

  private apiService = inject(ApiService);
  private loaderService = inject(LoaderService);
  private translateService = inject(TranslateService);
  private alertService = inject(AlertService);
  private headerService = inject(HeaderService);
  public permissionWrapperService = inject(PermissionWrapperService);

  public ModalMode = ModalMode;
  public DateUtils = DateUtils;
  PermissionsResources = PermissionsResources;
  PermissionsActions = PermissionsActions;
  configuration = signal<ConfigurationModel>(new ConfigurationModel());
  private originalConfiguration = signal<ConfigurationModel | null>(null);
  enableEdit = signal<boolean>(false);
  isSaving = signal<boolean>(false);
  fieldErrors = signal<Map<string, string[]>>(new Map());
  id = computed<number>(() => 1);

  endpoint = computed<string>(() => {
    return environment.API.CONFIGURATION_URI
  });

  editButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.PRIMARY,
    text: 'configurations.modify',
    icon: 'icon-edit',
    iconPosition: 'left',
    disabled: false,
  }));

  saveButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.PRIMARY,
    text: 'configurations.save_changes',
    icon: 'icon-save',
    iconPosition: 'left',
    disabled: this.isSaving()
  }));

  cancelButtonConfig = computed<ButtonConfig>(() => ({
    type: ButtonType.SECONDARY,
    text: 'configurations.cancel',
    disabled: this.isSaving()
  }));

  get generalFields(): ModalFieldConfig<ConfigurationModel>[] {
    return [
      {
        key: 'squareMetersPerLinearMeter',
        label: 'configuration.squareMetersPerLinearMeter',
        placeholder: 'configuration.squareMetersPerLinearMeter_placeholder',
        type: FieldType.NUMBER,
        readonly: false,
        validators: [Validators.required],
        errorMessages: {
          required: 'configuration.squareMetersPerLinearMeter_required'
        }
      },
      {
        key: 'defaultCurrency',
        label: 'configuration.defaultCurrency',
        placeholder: 'configuration.defaultCurrency_placeholder',
        type: FieldType.TEXT,
        readonly: false,
        errorMessages: {
          required: 'configuration.defaultCurrency_required'
        }
      }
    ];
  }

  ngOnInit(): void {
    this.loadConfiguration();
    this.setHeaderTitle();
  }

  ngOnDestroy(): void {
    this.headerService.clearCustomActions();
  }

  private setHeaderTitle(): void {
    this.translateService.get('configuration.title').subscribe(title => {
      this.headerService.setHeaderConfig({
        title: title,
        showDefaultFilter: false,
        showDefaultCreate: false,
        customActions: [
          {
            id: 'modify-configuration',
            label: this.translateService.instant('configurations.modify'),
            icon: 'icon-edit',
            class: 'c-btn',
            tooltip: 'Modificar',
            callback: () => this.onEnableEdit(),
          }
        ],
      });
    });
  }

  private loadConfiguration(): void {
    this.loaderService.showLoader();
    this.apiService.fetchData(this.endpoint() + '/' + this.id()).subscribe({
      next: (configuration) => {
        let configurationModel = new ConfigurationModel().fromJSON(configuration.data);
        this.configuration.set(configurationModel);
        this.loaderService.hideLoader();
      },
      error: (error) => {
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: this.translateService.instant('configuration.errors.load_error'),
          duration: 5000,
          inBottom: true
        });
      }
    });
  }

  onSaveClick(): void {
    this.saveConfiguration();
  }

  onCancelClick(): void {
    if (this.originalConfiguration()) {
      this.configuration.set(this.originalConfiguration()!);
    }
    this.enableEdit.set(false);
    this.headerService.updateCustomAction('modify-configuration', {
      disabled: false
    });
    this.fieldErrors.set(new Map());
  }

  onEnableEdit(): void {
    this.originalConfiguration.set(new ConfigurationModel().fromJSON(this.configuration()));
    this.enableEdit.set(true);
    this.headerService.updateCustomAction('modify-configuration', {
      disabled: true
    });
  }

  onDisableEdit(): void {
    if (this.originalConfiguration()) {
      this.configuration.set(this.originalConfiguration()!);
    }
    this.enableEdit.set(false);
    this.fieldErrors.set(new Map());
    this.headerService.updateCustomAction('modify-configuration', {
      disabled: false
    });
  }

  saveConfiguration(): void {
    if (!this.validateAllFields()) {
      this.alertService.show({
        type: AlertType.ERROR,
        message: this.translateService.instant('configuration.errors.validation_error'),
        duration: 5000,
        inBottom: true
      });
      return;
    }

    this.isSaving.set(true);
    this.loaderService.showLoader();

    this.apiService.partialUpdateObj(this.endpoint(), this.id(), this.configuration(), false, {
      'Content-Type': 'application/merge-patch+json'
    }).subscribe({
      next: (updatedConfiguration) => {
        let configurationModel = new ConfigurationModel().fromJSON(updatedConfiguration.data);
        this.configuration.set(configurationModel);
        this.enableEdit.set(false);
        this.isSaving.set(false);
        this.fieldErrors.set(new Map());
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.SUCCESS,
          message: this.translateService.instant('configuration.success.saved'),
          duration: 5000,
          inBottom: true
        });
        this.headerService.updateCustomAction('modify-configuration', {
          disabled: false
        });
      },
      error: (error) => {
        this.isSaving.set(false);
        this.loaderService.hideLoader();
        this.alertService.show({
          type: AlertType.ERROR,
          message: this.translateService.instant('configuration.errors.save_error'),
          duration: 5000,
          inBottom: true
        });
        this.headerService.updateCustomAction('modify-configuration', {
          disabled: false
        });
      }
    });
  }

  validateAllFields(): boolean {
    const config = this.configuration();

    if (!config.squareMetersPerLinearMeter || isNaN(Number(config.squareMetersPerLinearMeter)) || Number(config.squareMetersPerLinearMeter) < 1 || Number(config.squareMetersPerLinearMeter) > 10) {
      this.fieldErrors.update(errors => {
        const newErrors = new Map(errors);
        newErrors.set('squareMetersPerLinearMeter', ['configuration.errors.squareMetersPerLinearMeter_range']);
        return newErrors;
      });
      return false;
    }

    if (!config.defaultCurrency || config.defaultCurrency.trim() === '') {
      this.fieldErrors.update(errors => {
        const newErrors = new Map(errors);
        newErrors.set('defaultCurrency', ['configuration.errors.defaultCurrency_required']);
        return newErrors;
      });
      return false;
    }

    if (config.defaultCurrency.trim().length !== 3) {
      this.fieldErrors.update(errors => {
        const newErrors = new Map(errors);
        newErrors.set('defaultCurrency', ['configuration.errors.defaultCurrency_length']);
        return newErrors;
      });
      return false;
    }

    this.fieldErrors.set(new Map());
    return true;
  }

  getFieldErrors(key: string): string[] {
    return this.fieldErrors().get(key) || [];
  }

  onFieldValueChange(key: string, value: any): void {
    this.configuration.update((config) => {
      (config as any)[key] = value;
      return config;
    });
    this.clearFieldError(key);
  }

  private clearFieldError(key: string): void {
    this.fieldErrors.update(errors => {
      const newErrors = new Map(errors);
      newErrors.delete(key);
      return newErrors;
    });
  }
}
